import { dispatchLocalVectorizations } from "../utils/dispatchLocalVectorizations";
import { AddEventResponse } from "./types/eventTypes";
import { HttpRequest } from "./utils/HttpRequest";

const DEFAULT_EXTENSION_FILENAME = "passapp-rb-extension.zip";

export default class NewEvent {
  httpReq = new HttpRequest();
  //Services
  createInitialEvent(token: string, who: string): Promise<any> {
    return this.httpReq.post(`ws-security/createInitialEvent`, {
      token,
      who,
    });
  }

  /**
   * Unico punto por donde pasa addEvent en toda la app: lo llaman useActionEvent (Guardia) y
   * useEvent de LogisticPark (evento principal + acompañantes). Por eso el disparo de la
   * Raspberry se engancha aca y no en los call sites — cubre los tres y cualquiera futuro.
   * Mismo criterio que el onSuccess centralizado del totem (useAddEventMutation.ts:18).
   *
   * `dispatchLocalVectorizations` es fire-and-forget y no lanza nunca, asi que no puede
   * afectar el flujo de registro. Los rejects del POST pasan intactos.
   */
  addEventAll(event: any): Promise<any> {
    return this.httpReq.post(`ws-security/addEvent`, event).then((response) => {
      dispatchLocalVectorizations(response as AddEventResponse);
      return response;
    });
  }

  /**
   * Baja el .zip de la extension que hace el puente con la Raspberry y dispara la descarga.
   *
   * No usa `HttpRequest` a proposito: `requestAll` siempre hace `.json()` e ignora
   * `responseType`, asi que no puede traer un binario. Y como el endpoint es POST con el
   * token en el body, tampoco sirve un `<a href>` directo.
   *
   * El backend puede responder el binario o un JSON con la URL, asi que se decide por
   * Content-Type y se soportan los dos.
   */
  async downloadExtension(token: string): Promise<void> {
    const response = await fetch(`${process.env.REACT_APP_BASE_URL}ws-security/downloadExtension`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status} al descargar la extension`);
    }

    const contentType = response.headers.get("content-type") || "";

    // Caso JSON: { status, data: { url } } — o un error con status 200.
    if (contentType.includes("application/json")) {
      const json = await response.json();
      if (json?.status === "error") {
        throw new Error(json?.msg || "El backend rechazo la descarga");
      }
      const url = json?.data?.url || json?.url;
      if (!url) {
        throw new Error("El backend no devolvio la URL de la extension");
      }
      triggerDownload(url, DEFAULT_EXTENSION_FILENAME, false);
      return;
    }

    // Caso binario: el zip viene en el body.
    const blob = await response.blob();
    if (!blob.size) {
      throw new Error("El backend devolvio un archivo vacio");
    }
    const objectUrl = URL.createObjectURL(blob);
    try {
      triggerDownload(objectUrl, filenameFromResponse(response), true);
    } finally {
      // Se revoca en el siguiente tick: si se revoca ya, Chrome cancela la descarga.
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    }
  }
}

function filenameFromResponse(response: Response): string {
  const disposition = response.headers.get("content-disposition") || "";
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (!match) return DEFAULT_EXTENSION_FILENAME;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function triggerDownload(url: string, filename: string, isBlob: boolean): void {
  const anchor = document.createElement("a");
  anchor.href = url;
  if (isBlob) {
    anchor.download = filename;
  } else {
    // URL remota: `download` no aplica cross-origin, asi que se abre y el server decide.
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
  }
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}
